import { compareChecklistRows } from "@totodo/operations"
import type { DbObject } from "@totodo/operations"

/**
 * `overrides` with `patch` merged into the row `id`'s pending override — merged, never replaced.
 * The row may already carry an edit the database has not reflected yet: above all a new index
 * from the renumbering an insert next to it needed (a split, Enter, paste), or a title. Replacing
 * the override drops that edit, and the row shows out of place or with its old text until the
 * live query catches up. (An override clears once the row in the database has every field of it.)
 */
export function mergeOverride(
  overrides: Record<string, Partial<DbObject>>,
  id: string,
  patch: Partial<DbObject>,
): Record<string, Partial<DbObject>> {
  return { ...overrides, [id]: { ...overrides[id], ...patch } }
}

/**
 * The checklist as ChecklistContainer shows it: the real rows (queryChecklistItems) with the
 * pending optimistic edits applied — deletes removed, overrides (a dragged row's new index, a
 * split row's new title) merged in, adds included — in the shared display order
 * (compareChecklistRows). With nothing pending that is exactly the order get_task_detail and REST
 * report, malformed rows included: rows without a usable index go last, not first.
 */
export function mergeItems(
  realItems: DbObject[],
  adds: Record<string, DbObject>,
  dels: Record<string, true>,
  overrides: Record<string, Partial<DbObject>> = {},
): DbObject[] {
  const withOverride = (item: DbObject): DbObject =>
    overrides[item.id!] ? { ...item, ...overrides[item.id!] } : item
  const merged = realItems.filter((i) => !dels[i.id!]).map(withOverride)
  // Pending adds: skip items that are also pending delete (rapid add+delete) or already real
  for (const item of Object.values(adds)) {
    if (!dels[item.id!] && !merged.some((m) => m.id === item.id)) {
      merged.push(withOverride(item))
    }
  }
  // Sorted, so that overrides (e.g. an optimistic DnD reorder) show in the order too
  return merged.sort(compareChecklistRows)
}
